// @ts-nocheck
import dayjs from 'dayjs';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { cloneDeep } from 'lodash';
import { useState } from 'react';
import { useChatFeatureEnabled } from '@nile/components/src/Pages/SystemTrayChatPage/utils';
import { postMessageToWails } from '@nile/components/src/Pages/SystemTrayChatPage/constants';

interface User {
  name: string;
  userId: string;
  avatarFileName: string;
}

interface ConcurrentViewer {
  user: User;
  state: 'ACTIVE' | 'INACTIVE' | string;
  isTyping: boolean;
}

type eventType =
  | 'UPDATE'
  | 'EXIT_PAGE'
  | 'REGISTER_PAGE'
  | 'UNREGISTER_PAGE'
  | 'TYPING_CONVERSATION'
  | 'UPDATE_CONVERSATION'
  | 'STARTED_CHAT'
  | 'NOTIFY_COLLABORATORS_INFO'
  | 'STOPPED_TYPING_CONVERSATION';

interface PayloadType {
  event: eventType;
  page: {
    type: 'DETAIL';
    entity: string;
    recordId: string;
  };
  epochMillis: string;
  type: 'SERVER_EVENT' | 'CLIENT_EVENT';
}

interface RequestPayloadType extends PayloadType {
  data: {
    allowSimultaneousPage: boolean;
  };
}

interface ResponsePayloadType extends PayloadType {
  user: User;
  data: {
    allowSimultaneousPage: boolean;
    collaborators: ConcurrentViewer[];
  };
}

interface RequestEventDataType {
  context: string;
  // windowsId: string;
  payload: RequestPayloadType;
}

interface ResponseEventDataType extends RequestEventDataType {
  payload: ResponsePayloadType;
}

let broadcastChannel: BroadcastChannel;
if (BroadcastChannel) {
  broadcastChannel = new BroadcastChannel('SuperShare');
}

export default function getUpdates(
  updateId: string
): {
  concurrentAsset: any[];
  concurrentViewers: ConcurrentViewer[];
  sendClientEvent: ({
    type,
    connectionId,
    event,
    id,
    entity,
    threadId,
    data,
    checkForSession,
    epochMillis,
    context,
    isWeb
  }: {
    type: string;
    connectionId: string;
    event: eventType;
    id: string;
    threadId: string;
    entity: string;
    data: any;
    checkForSession: boolean;
    epochMillis: Date;
    context: string;
    isWeb: any;
  }) => void;
} {
  const flags = useFlags();
  const [concurrentViewers, setConcurrentViewers] = useState([]);
  const [concurrentAssets, setConcurrentAsset] = useState([]);
  const [ruleStatus, setRuleStatus] = useState({});
  const isChatAvailable = useChatFeatureEnabled();

  function sendEvent({
    type = 'CLIENT_EVENT',
    connectionId,
    event,
    id,
    entity,
    threadId,
    data = null,
    checkForSession = true,
    epochMillis = null,
    context = 'pageUpdate',
    isWeb = true
  }) {
    const uniqueIdRegistered = sessionStorage.getItem(id);
    if (event === 'REGISTER_PAGE') {
      if (uniqueIdRegistered === 'REGISTER_PAGE' && checkForSession) {
        return;
      }
      sessionStorage.setItem(id, 'REGISTER_PAGE');
    }

    if (
      (event === 'EXIT_PAGE' || event === 'UNREGISTER_PAGE') &&
      uniqueIdRegistered
    ) {
      sessionStorage.removeItem(id);
    }
    let msgData;
    if (context === 'chat') {
      msgData = {
        context: context,
        payload: {
          type: type || 'CLIENT_EVENT',
          page: {
            type: 'DETAIL',
            entity: entity || 'Ticket',
            recordId: id,
            component: {
              type: 'DETAIL',
              entity: 'Chat',
              recordId: threadId
            }
          },
          data: data,
          event: event || 'CONVERSATION_MESSAGE',
          epochMillis:
            `${epochMillis}` ||
            `${dayjs()
              .utc()
              .valueOf()}`
        }
      };
    } else {
      msgData = {
        context: context,
        payload: {
          type: 'CLIENT_EVENT',
          page: {
            type: 'DETAIL',
            entity: entity || 'Ticket',
            recordId: id
          },
          data: data,
          event: event || 'REGISTER_PAGE',
          epochMillis:
            epochMillis ||
            dayjs()
              .utc()
              .valueOf()
        }
      };
    }
    const postMessageData: RequestEventDataType = msgData;
    /** New changes, push the message to agent via wails */
    if (isWeb) {
      broadcastChannel.postMessage(
        JSON.stringify({
          name: 'wsdata',
          data: postMessageData,
          connectionId: connectionId
        })
      );
    } else {
      postMessageToWails({
        eventName: 'SEND_MESSAGE',
        data: postMessageData
      });
    }
  }

  broadcastChannel.onmessage = (event) => {
    if (flags?.agentChatFe && isChatAvailable && event.data && updateId) {
      const eventData: ResponseEventDataType = JSON.parse(event.data);
      const { context, payload } = eventData;
      if (
        context === 'pageUpdate' &&
        payload?.page?.recordId === updateId &&
        payload?.type === 'SERVER_EVENT'
      ) {
        const clonedConcurrentViewers = cloneDeep(concurrentViewers);
        switch (payload.event) {
          case 'NOTIFY_COLLABORATORS_INFO':
            setConcurrentViewers(payload.data.collaborators);
            break;
          case 'ADVANCE_POLICY_RULE_OPERATION':
            setRuleStatus(payload.data);
            break;
          case 'TICKET_ASSET_REGISTER':
            setConcurrentAsset(payload.data.assets);
            break;
          case 'REGISTER_PAGE':
            const registeredUserIndex = clonedConcurrentViewers.findIndex(
              (viewer) => {
                return viewer.user.userId === payload.user?.userId;
              }
            );
            if (registeredUserIndex > -1) {
              clonedConcurrentViewers[registeredUserIndex].state = 'ACTIVE';
            } else {
              clonedConcurrentViewers.push({
                user: payload.user,
                state: 'ACTIVE',
                isTyping: false
              });
            }
            setConcurrentViewers(clonedConcurrentViewers);
            break;
          case 'EXIT_PAGE':
            setConcurrentViewers(
              clonedConcurrentViewers.map((currentViewer) => {
                if (currentViewer.user.userId === payload.user.userId) {
                  return {
                    ...currentViewer,
                    state: 'INACTIVE',
                    isTyping: false
                  };
                }
                return currentViewer;
              })
            );
            break;
          case 'UNREGISTER_PAGE':
            setConcurrentViewers(
              clonedConcurrentViewers.filter(
                (currentViewer) =>
                  currentViewer.user.userId !== payload.user.userId
              )
            );
            break;
          default:
            break;
        }
      }
    } else {
      const eventData: ResponseEventDataType = JSON.parse(event.data);
      const { context, payload } = eventData;
      if (
        context === 'pageUpdate' &&
        payload?.page?.recordId === updateId &&
        payload?.type === 'SERVER_EVENT'
      ) {
        switch (payload.event) {
          case 'ADVANCE_POLICY_RULE_OPERATION':
            setRuleStatus(payload.data);
            break;
          default:
            break;
        }
      }
    }
  };

  return {
    concurrentViewers: concurrentViewers,
    concurrentAsset: concurrentAssets,
    ruleStatus: ruleStatus,
    sendClientEvent: sendEvent
  };
}
